export interface HttpLikeError extends Error {
  status?: number;
  code?: string;
}

export function createHttpError(
  message: string,
  options?: {
    status?: number;
    code?: string;
  },
): HttpLikeError {
  const error = new Error(message) as HttpLikeError;
  if (options?.status !== undefined) {
    error.status = options.status;
  }
  if (options?.code) {
    error.code = options.code;
  }
  return error;
}

export function isNotFoundLikeError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const maybe = error as { status?: unknown; code?: unknown; message?: unknown };
  const status = typeof maybe.status === 'number' ? maybe.status : undefined;
  if (status === 404) {
    return true;
  }

  const code = typeof maybe.code === 'string' ? maybe.code.toLowerCase() : '';
  if (code === 'not_found' || code === 'notfound') {
    return true;
  }

  const message = typeof maybe.message === 'string' ? maybe.message.toLowerCase() : '';
  return message.includes('404') || message.includes('not found');
}

/**
 * canonical envelope（ADR-0031）と旧フラット形の両方からエラー情報を抽出する。
 *
 * 優先順:
 * - code:    `body.data.error` → `body.code`（旧 handleAPIError 形式）
 * - message: `body.data.message` → `body.error`（フラット形メッセージ）
 *
 * 移行期間中は旧形と新形が混在するため、hook 側はこの関数を経由して
 * 「どちらを返してもメッセージが取れる」状態を保つ。
 */
export function readErrorEnvelope(body: unknown): { code?: string; message?: string } {
  if (!body || typeof body !== 'object') {
    return {};
  }

  const record = body as Record<string, unknown>;
  let code: string | undefined;
  let message: string | undefined;

  const data = record.data;
  if (data && typeof data === 'object') {
    const dataRecord = data as Record<string, unknown>;
    if (typeof dataRecord.error === 'string') {
      code = dataRecord.error;
    }
    if (typeof dataRecord.message === 'string') {
      message = dataRecord.message;
    }
  }

  if (!code && typeof record.code === 'string') {
    code = record.code;
  }

  if (!message && typeof record.error === 'string') {
    // フラット形 `{ error: '...' }` の場合、`error` はコードではなく人間可読メッセージとして
    // 使われていることが多いので message にマップする。
    message = record.error;
  }

  return { code, message };
}
